/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
export class Task {
    private _id: string;
    private _callback: any;
    private _tickTime = 0;
    private _counter = 0;

    public get id(): string {
        return this._id;
    }
    public set id(value: string) {
        this._id = value;
    }

    public get callback(): any {
        return this._callback;
    }
    public set callback(value: any) {
        this._callback = value;
    }

    public get tickTime(): number {
        return this._tickTime;
    }
    public set tickTime(value: number) {
        this._tickTime = value;
    }

    public get counter(): number {
        return this._counter;
    }
    public set counter(value: number) {
        this._counter = value;
    }

    constructor(id: string, callback: any, ticks: number) {
        this._id = id;
        this._callback = callback;
        this._tickTime = ticks;
    }
}

export class TaskTimer {
    private _taskMap: Map<string, Task> = new Map<string, Task>();
    private _criticalTaskMap: Map<string, Task> = new Map<string, Task>();
    private _isPaused = false;

    constructor(timer: number, initAfter = 0) {
        setTimeout(() => {
            setInterval(() => {
                // ... do something now
                if (this._isPaused == false) {
                    this.executeTasks(this._taskMap);
                } else {
                    this.executeTasks(this._criticalTaskMap);
                }
            }, timer);
        }, initAfter);
    }

    private executeTasks(map: Map<string, Task>) {
        const dispatchCallbackFn = async (callback: any) => {
            callback();
        };

        for (const [, task] of map) {
            task.counter = task.counter + 1;
            if (task.tickTime != 0 && task.counter >= task.tickTime) {
                if (task.callback) {
                    dispatchCallbackFn(task.callback);
                }
                task.counter = 0;
            }
        }
    }

    public addTask(id: string, callback: any, tickTime: number, isCriticalTask: boolean) {
        const task = new Task(id, callback, tickTime);
        this._taskMap.set(id, task);
        if (isCriticalTask) {
            this._criticalTaskMap.set(id, task);
        }
    }

    public pause() {
        this._isPaused = true;
    }

    public resume() {
        this._isPaused = false;
        const dispatchCallbackFn = async (callback: any) => {
            callback();
        };
        for (const [, task] of this._taskMap) {
            if (task.callback) {
                dispatchCallbackFn(task.callback);
            }
        }
    }

    public isPaused() {
        return this._isPaused;
    }

    public cancel() {
        return this._isPaused;
    }

    public getTask(id: string) {
        let task = this._taskMap.get(id);
        if (!task) {
            task = new Task("", undefined, 0);
        }
        return task;
    }
}
